/**
 * ChainVault WP - Multisender / Airdrop Sender
 * Bulk send native coins or tokens to multiple addresses
 *
 * @package ChainVault
 * @since   1.0.0
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';
import './LaunchpadPage.css';

const NETWORKS = [
    { id: 'ethereum', label: 'Ethereum', symbol: 'ETH',  color: '#627EEA', type: 'evm',    chainId: 1,    rpc: 'https://ethereum-rpc.publicnode.com'    },
    { id: 'bsc',      label: 'BSC',      symbol: 'BNB',  color: '#F0B90B', type: 'evm',    chainId: 56,   rpc: 'https://bsc-dataseed.binance.org'       },
    { id: 'polygon',  label: 'Polygon',  symbol: 'MATIC',color: '#8247E5', type: 'evm',    chainId: 137,  rpc: 'https://polygon-bor-rpc.publicnode.com' },
    { id: 'base',     label: 'Base',     symbol: 'ETH',  color: '#0052FF', type: 'evm',    chainId: 8453, rpc: 'https://mainnet.base.org'               },
    { id: 'solana',   label: 'Solana',   symbol: 'SOL',  color: '#9945FF', type: 'solana', chainId: null, rpc: 'https://api.mainnet-beta.solana.com'    },
];

function isValidEVM( addr )    { return /^0x[0-9a-fA-F]{40}$/.test( addr ); }
function isValidSolana( addr ) { return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test( addr ); }
function validateAddress( addr, networkType ) {
    if ( networkType === 'evm' )    return isValidEVM( addr );
    if ( networkType === 'solana' ) return isValidSolana( addr );
    return false;
}

export default function MultisenderPage() {
    const navigate = useNavigate();
    const { builtInWallet, walletType } = useWallet();

    const [ network,   setNetwork   ] = useState( NETWORKS[0] );
    const [ tokenMode, setTokenMode ] = useState( 'native' );
    const [ tokenAddr, setTokenAddr ] = useState( '' );
    const [ rawInput,  setRawInput  ] = useState( '' );
    const [ equalAmt,  setEqualAmt  ] = useState( '' );
    const [ equalMode, setEqualMode ] = useState( false );
    const [ parsed,    setParsed    ] = useState( [] );
    const [ isSending, setIsSending ] = useState( false );
    const [ results,   setResults   ] = useState( [] );
    const [ step,      setStep      ] = useState( 'input' );

    const parseInput = useCallback( () => {
        const lines = rawInput.split( '\n' ).map( l => l.trim() ).filter( Boolean );
        const entries = lines.map( line => {
            const parts = line.split( /[,\s]+/ );
            const addr  = parts[0] || '';
            const amt   = equalMode ? equalAmt : ( parts[1] || '' );
            return { address: addr, amount: amt, valid: validateAddress( addr, network.type ) };
        });
        setParsed( entries );
        if ( entries.length > 0 ) setStep( 'preview' );
        else toast.error( 'No valid entries found.' );
    }, [ rawInput, equalMode, equalAmt, network ] );

    const validEntries   = parsed.filter( e => e.valid );
    const invalidEntries = parsed.filter( e => !e.valid );
    const totalAmount    = validEntries.reduce( ( s, e ) => s + parseFloat( e.amount || 0 ), 0 );

    const removeInvalid = () => setParsed( p => p.filter( e => e.valid ) );
    const removeEntry   = ( i ) => setParsed( p => p.filter( (_, idx) => idx !== i ) );

    const sendEVM = async () => {
        const { ethers } = await import( 'ethers' );
        let signer;
        if ( walletType === 'metamask' && window.ethereum ) {
            const provider = new ethers.BrowserProvider( window.ethereum );
            signer = await provider.getSigner();
        } else if ( builtInWallet && builtInWallet.ethereum ) {
            const provider = new ethers.JsonRpcProvider( network.rpc );
            signer = new ethers.Wallet( builtInWallet.ethereum.privateKey, provider );
        } else { throw new Error( 'No EVM wallet connected.' ); }

        let contract, decimals = 18;
        if ( tokenMode === 'token' && tokenAddr ) {
            const abi = [ 'function transfer(address,uint256) returns(bool)', 'function decimals() view returns(uint8)' ];
            contract = new ethers.Contract( tokenAddr, abi, signer );
            decimals = Number( await contract.decimals() );
        }

        const res = [];
        for ( const entry of validEntries ) {
            try {
                const amt = ethers.parseUnits( String( entry.amount ), decimals );
                const tx  = tokenMode === 'native'
                    ? await signer.sendTransaction({ to: entry.address, value: amt })
                    : await contract.transfer( entry.address, amt );
                res.push({ address: entry.address, amount: entry.amount, status: 'success', tx: tx.hash });
                toast.success( entry.address.slice(0,8) + '... sent!' );
            } catch(err) {
                res.push({ address: entry.address, amount: entry.amount, status: 'failed', tx: '', error: err.message });
                toast.error( entry.address.slice(0,8) + '... failed' );
            }
        }
        return res;
    };

    const sendSolana = async () => {
        const web3 = await import( '@solana/web3.js' );
        const isTestnet = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
        const rpc = isTestnet ? 'https://api.devnet.solana.com' : 'https://api.mainnet-beta.solana.com';
        const connection = new web3.Connection( rpc, 'confirmed' );
        if ( !builtInWallet?.solana?.secretKey ) throw new Error( 'Built-in Solana wallet required.' );
        const payer = web3.Keypair.fromSecretKey( new Uint8Array( builtInWallet.solana.secretKey ) );

        const res = [];
        const BATCH = 10;
        for ( let i = 0; i < validEntries.length; i += BATCH ) {
            const batch = validEntries.slice( i, i + BATCH );
            try {
                const tx = new web3.Transaction();
                for ( const entry of batch ) {
                    tx.add( web3.SystemProgram.transfer({
                        fromPubkey: payer.publicKey,
                        toPubkey:   new web3.PublicKey( entry.address ),
                        lamports:   Math.floor( parseFloat( entry.amount ) * web3.LAMPORTS_PER_SOL ),
                    }));
                }
                const { blockhash } = await connection.getLatestBlockhash();
                tx.recentBlockhash = blockhash;
                tx.feePayer = payer.publicKey;
                tx.sign( payer );
                /* Send via PHP relay to avoid browser RPC rate limits */
                const wpBase = window.chainvaultSettings?.siteUrl || '/wp-json/chainvault/v1';
                const serialized = tx.serialize();
                const txB64 = btoa( String.fromCharCode( ...new Uint8Array( serialized ) ) );
                const relayRes  = await fetch( wpBase + '/solana/relay', {
                    method:  'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body:    JSON.stringify({ transaction: txB64, network: isTestnet ? 'devnet' : 'mainnet' }),
                });
                const relayData = await relayRes.json();
                console.log( 'Relay response:', JSON.stringify( relayData ) );
                if ( relayData.error ) throw new Error( JSON.stringify( relayData.error ) );
                const sig = relayData.signature;
                await connection.confirmTransaction( sig, 'confirmed' );
                batch.forEach( entry => {
                    res.push({ address: entry.address, amount: entry.amount, status: 'success', tx: sig });
                    toast.success( entry.address.slice(0,8) + '... sent!' );
                });
            } catch(err) {
                batch.forEach( entry => res.push({ address: entry.address, amount: entry.amount, status: 'failed', tx: '', error: err.message }) );
                toast.error( 'Batch failed: ' + err.message.slice(0,40) );
            }
        }
        return res;
    };

    const handleSend = async () => {
        if ( validEntries.length === 0 ) { toast.error( 'No valid recipients.' ); return; }
        setIsSending( true );
        try {
            const res = network.type === 'evm' ? await sendEVM() : await sendSolana();
            setResults( res );
            setStep( 'done' );
        } catch(err) {
            toast.error( err.message.slice(0,100) );
        } finally { setIsSending( false ); }
    };

    const reset = () => { setRawInput(''); setEqualAmt(''); setParsed([]); setResults([]); setStep('input'); setTokenAddr(''); };

    const successCount = results.filter( r => r.status === 'success' ).length;
    const failCount    = results.filter( r => r.status === 'failed'  ).length;
    const isTestnetMode = localStorage.getItem( 'chainvault_testnet_mode' ) === '1';
    const explorerTx   = network.id === 'solana'
        ? 'https://solscan.io/tx/'
        : network.id === 'bsc' ? 'https://bscscan.com/tx/' : network.id === 'polygon' ? 'https://polygonscan.com/tx/' : network.id === 'base' ? 'https://basescan.org/tx/' : 'https://etherscan.io/tx/';
    const explorerSuffix = network.id === 'solana' && isTestnetMode ? '?cluster=devnet' : '';

    const btnStyle = ( active, color ) => ({
        padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
        border: '2px solid ' + ( active ? color : 'rgba(255,255,255,0.1)' ),
        background: active ? color + '22' : 'transparent',
        color: active ? color : '#aaa',
    });

    if ( step === 'done' ) return (
        <div className="lp-page">
            <div className="lp-back" onClick={()=>navigate('/launchpad')}>← Back to Launchpad</div>
            <div className="lp-success-card">
                <div className="lp-success-icon">{ successCount === results.length ? '🎉' : '⚠️' }</div>
                <h2>Airdrop Complete 🎉</h2>
                <p>{ successCount } sent successfully{ failCount > 0 ? ', ' + failCount + ' failed' : '' }</p>
                <div style={{ width:'100%', maxHeight:'300px', overflowY:'auto', marginTop:'16px' }}>
                    { results.map( (r,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', marginBottom:'6px', borderRadius:'8px', background: r.status==='success' ? 'rgba(0,200,120,0.1)' : 'rgba(220,50,50,0.1)', fontSize:'12px' }}>
                            <span style={{ color:'#aaa', fontFamily:'monospace' }}>{ r.address.slice(0,10)+'...'+r.address.slice(-6) }</span>
                            <span style={{ color:'#fff' }}>{ r.amount } { network.symbol }</span>
                            { r.tx ? <a href={explorerTx+r.tx+explorerSuffix} target="_blank" rel="noopener noreferrer" style={{ color:'#7C63FF', fontSize:'11px' }}>View ↗</a> : <span style={{ color:'#ff5577' }}>Failed</span> }
                        </div>
                    ))}
                </div>
                <button className="lp-btn-outline" style={{ marginTop:'20px' }} onClick={reset}>Send Another Batch</button>
            </div>
        </div>
    );

    return (
        <div className="lp-page">
            <div className="lp-back" onClick={()=>navigate('/launchpad')}>← Back to Launchpad</div>
            <div className="lp-creator-wrap">
                <div className="lp-creator-main">
                    <div className="lp-creator-header">
                        <div className="lp-creator-badge" style={{ background:'linear-gradient(135deg,#7C63FF,#FC72FF)' }}>MULTISENDER</div>
                        <h1>Airdrop tokens to multiple wallets at once</h1>
                        <p>Send native coins or tokens to hundreds of wallets at once. Invalid addresses for the selected network are automatically flagged.</p>
                    </div>
                    <div className="lp-form-card">
                        <div className="lp-section-title">Select Network</div>
                        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'20px' }}>
                            { NETWORKS.map( n => (
                                <button key={n.id} style={{ ...btnStyle( network.id===n.id, n.color ), borderRadius:'20px' }}
                                    onClick={()=>{ setNetwork(n); setParsed([]); setStep('input'); }}>{ n.label }</button>
                            ))}
                        </div>

                        <div className="lp-section-title">Token</div>
                        <div style={{ display:'flex', gap:'10px', marginBottom:'16px' }}>
                            <button style={btnStyle(tokenMode==='native', network.color)} onClick={()=>setTokenMode('native')}>Native ({ network.symbol })</button>
                            <button style={btnStyle(tokenMode==='token',  network.color)} onClick={()=>setTokenMode('token')}>Custom Token</button>
                        </div>
                        { tokenMode === 'token' && (
                            <div className="lp-field" style={{ marginBottom:'20px' }}>
                                <label>{ network.type==='evm' ? 'Token Contract Address' : 'Token Mint Address' }</label>
                                <input className="lp-input" placeholder={ network.type==='evm' ? '0x...' : 'Mint address...' } value={tokenAddr} onChange={e=>setTokenAddr(e.target.value)} />
                            </div>
                        )}

                        <div className="lp-section-title">Amount Mode</div>
                        <div style={{ display:'flex', gap:'10px', marginBottom:'16px' }}>
                            <button style={btnStyle(!equalMode, network.color)} onClick={()=>setEqualMode(false)}>Per address (address, amount)</button>
                            <button style={btnStyle( equalMode, network.color)} onClick={()=>setEqualMode(true)}>Same amount for all</button>
                        </div>
                        { equalMode && (
                            <div className="lp-field" style={{ marginBottom:'16px' }}>
                                <label>Amount per recipient ({ network.symbol })</label>
                                <input className="lp-input" type="number" placeholder="100" value={equalAmt} onChange={e=>setEqualAmt(e.target.value)} />
                            </div>
                        )}

                        <div className="lp-section-title">Recipients</div>
                        <p style={{ fontSize:'12px', color:'#888', marginBottom:'8px' }}>
                            { equalMode ? 'One address per line. Invalid ' + (network.type==='evm'?'Solana':'EVM') + ' addresses will be flagged.' : 'Format: address, amount — one per line.' }
                        </p>
                        <textarea className="lp-input" rows={10}
                            placeholder={ network.type==='evm' ? (equalMode?'0xABC...123\n0xDEF...456':'0xABC...123, 0.5\n0xDEF...456, 1.0') : (equalMode?'SolAddr1...\nSolAddr2...':'SolAddr1..., 100\nSolAddr2..., 50') }
                            value={rawInput} onChange={e=>setRawInput(e.target.value)}
                            style={{ fontFamily:'monospace', fontSize:'13px' }} />

                        { step === 'input' && (
                            <button className="lp-btn-primary" style={{ marginTop:'16px', background:'linear-gradient(135deg,#7C63FF,#FC72FF)', border:'none' }}
                                onClick={parseInput} disabled={!rawInput.trim()||(equalMode&&!equalAmt)}>
                                Parse Recipients →
                            </button>
                        )}

                        { step === 'preview' && parsed.length > 0 && (
                            <div style={{ marginTop:'20px' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
                                    <div className="lp-section-title" style={{ margin:0 }}>{ parsed.length } recipients parsed</div>
                                    { invalidEntries.length > 0 && (
                                        <button onClick={removeInvalid} style={{ padding:'6px 14px', borderRadius:'8px', border:'none', background:'#ff5577', color:'#fff', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>
                                            Remove { invalidEntries.length } invalid ✕
                                        </button>
                                    )}
                                </div>
                                <div style={{ maxHeight:'300px', overflowY:'auto', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px' }}>
                                    { parsed.map( (entry,i) => (
                                        <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', background: entry.valid?'transparent':'rgba(255,50,50,0.08)' }}>
                                            <span style={{ fontSize:'14px' }}>{ entry.valid ? '✅' : '❌' }</span>
                                            <span style={{ flex:1, fontFamily:'monospace', fontSize:'12px', color: entry.valid?'#fff':'#ff7788' }}>{ entry.address }</span>
                                            <span style={{ color:'#00e5a0', fontSize:'13px', fontWeight:'600', whiteSpace:'nowrap' }}>{ entry.amount } { network.symbol }</span>
                                            <button onClick={()=>removeEntry(i)} style={{ background:'none', border:'none', color:'#666', cursor:'pointer', fontSize:'16px', padding:'0 4px' }}>×</button>
                                        </div>
                                    ))}
                                </div>

                                { invalidEntries.length === 0 && (
                                    <div style={{ marginTop:'16px', padding:'16px', background:'rgba(124,99,255,0.1)', borderRadius:'10px', border:'1px solid rgba(124,99,255,0.3)' }}>
                                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
                                            <span style={{ color:'#aaa' }}>Total recipients</span>
                                            <span style={{ color:'#fff', fontWeight:'700' }}>{ validEntries.length }</span>
                                        </div>
                                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                                            <span style={{ color:'#aaa' }}>Total amount</span>
                                            <span style={{ color:'#00e5a0', fontWeight:'700' }}>{ totalAmount.toFixed(4) } { network.symbol }</span>
                                        </div>
                                    </div>
                                )}

                                { invalidEntries.length > 0 && (
                                    <div style={{ marginTop:'12px', padding:'12px 16px', background:'rgba(255,80,80,0.1)', borderRadius:'10px', border:'1px solid rgba(255,80,80,0.3)', fontSize:'13px', color:'#ff8899' }}>
                                        ⚠️ { invalidEntries.length } invalid address{ invalidEntries.length>1?'es':'' } detected for { network.label }. Remove them before sending.
                                    </div>
                                )}

                                <div style={{ display:'flex', gap:'10px', marginTop:'16px' }}>
                                    <button className="lp-btn-outline" onClick={()=>setStep('input')}>← Edit</button>
                                    { invalidEntries.length === 0 && (
                                        <button className="lp-btn-primary" style={{ flex:1, background:'linear-gradient(135deg,#7C63FF,#FC72FF)', border:'none' }}
                                            onClick={handleSend} disabled={isSending}>
                                            { isSending ? 'Sending... (' + network.symbol + ')' : 'Send to ' + validEntries.length + ' wallets →' }
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lp-creator-side">
                    <div className="lp-fee-card" style={{ borderColor:'#7C63FF44' }}>
                        <div className="lp-fee-label">SELECTED NETWORK</div>
                        <div className="lp-fee-row"><span>Network:</span><strong style={{ color:network.color }}>{ network.label }</strong></div>
                        <div className="lp-fee-row"><span>Token:</span><strong>{ tokenMode==='native' ? network.symbol+' (native)' : 'Custom token' }</strong></div>
                        <div className="lp-fee-row"><span>Recipients:</span><strong>{ validEntries.length || '—' }</strong></div>
                        <div className="lp-fee-row"><span>Total:</span><strong style={{ color:'#00e5a0' }}>{ validEntries.length ? totalAmount.toFixed(4)+' '+network.symbol : '—' }</strong></div>
                    </div>
                    <div className="lp-guide-card">
                        <h3>How It Works</h3>
                        <div style={{ fontSize:'13px', color:'#aaa', lineHeight:'1.8' }}>
                            <p>1. Select your network</p>
                            <p>2. Choose native coin or paste a token address</p>
                            <p>3. Paste addresses one per line</p>
                            <p>4. Use equal mode to send same amount to all</p>
                            <p>5. Invalid addresses flagged automatically</p>
                            <p>6. Review summary then send</p>
                        </div>
                    </div>
                    <div className="lp-info-box">
                        <h4>Address Validation</h4>
                        <p>EVM addresses (0x...) only valid on ETH, BSC, Polygon, Base. Solana addresses only valid on Solana network.</p>
                    </div>
                    <div className="lp-info-box">
                        <h4>Solana Batching</h4>
                        <p>Solana sends are batched 10 per transaction for efficiency and lower fees.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
